import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import SnButton from "@shared/components/SnButton";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import { RowNode } from "ag-grid-community";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_PRD_BAS_REG_001_CHEM_POP_001 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });
    const parentref = modalRef.current.getPopupParent();
    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_BAS_REG_001_CHEM_POP_001",
            {}
          );
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
          });

          await onSearch(mergedData);
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onSearch = async (searchData?: any) => {
      try {
        const param = {
          id: "sn_prd_bas_reg_001_chem_pop_001_01",
          params: searchData || searchRef.current.getFormdata(),
        };
        gridRefs.current["grid1"].current?.resetUpdate();
        gridRefs.current["grid2"].current?.resetUpdate();
        const data: any = await fnGetDataSet(param);
        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
        });
      } catch (error: any) {}
    };

    //#region onSave
    const onSave = async () => {
      try {
        gridRefs.current["grid2"].current?.stopEditing();
        // L 필수값 체크
        const requiredValuesCheckL =
          gridRefs.current["grid2"].current.requiredValuesCheck();
        if (!requiredValuesCheckL) {
          return false;
        }

        const recipeparamChanges =
          gridRefs.current["grid2"].current.getChange();

        if (recipeparamChanges.modifiedRowcount === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }

        // 요청 데이터 구성
        const SaveData = {
          recipeparam: recipeparamChanges,
        };

        // 저장 API 호출
        const ok = await fnSave(
          "SN_PRD_BAS_REG_001_CHEM_POP_001_SAVE_01",
          SaveData
        );
        if (!ok) return false;

        // 성공적으로 저장된 후 초기화
        gridRefs.current["grid2"].current.resetUpdate();
        return true;
      } catch (error: any) {
        gridRefs.current["grid2"].current.resetUpdate(true);
        return false;
      }
    };
    //#endregion

    //#region onCellValueChangedH
    const onCellValueChangedH = async (event: any) => {
      if (!event) return;

      const colId = event.colId;
      const rowData = event.rowData;

      switch (colId) {
        case "yn_check":
          gridRefs.current["grid2"].current?.forEachFilteredNode(
            (node: RowNode) => {
              node.setDataValue("yn_check", rowData.yn_check);
            }
          );
          break;
      }
    };
    //#endregion

    //#region grid2Btns
    const onGrid2BtnsClick = async (id: string) => {
      switch (id) {
        case "addRow":
          return gridRefs.current["grid2"].current?.addRow();
        case "deleteRow":
          if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) {
            return;
          }

          const focusedRowNode =
            gridRefs.current["grid2"].current.focusedRowNode();
          const insertRows =
            gridRefs.current["grid2"].current.getChange().insert ?? [];
          const isInsertRow = insertRows.some(
            (row: any) => row.cd_equip === focusedRowNode.data.cd_equip
          );

          if (!isInsertRow) {
            showDialog("alter", "신규 행만 삭제할 수 있습니다.");
            return false;
          }

          gridRefs.current["grid2"].current?.deleteSelectedRows();
          break;
        case "btnSave":
          onSave();
          break;
      }
    };
    //#endregion

    //#region onBeforeAddRow2
    const onBeforeAddRow2 = (): any => {
      try {
        const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
        if (focusedNodeH == null) {
          showDialog("alter", "선택된 행이 없습니다.");
          return false;
        }
        const searchInfo = searchRef.current.getFormdata();
        const maxNoLine =
          gridRefs.current["grid2"].current.getMaxValue("no_line_param");
        const maxSeq = gridRefs.current["grid2"].current.getMaxValue("no_seq");
        const updatedRow = {
          cd_firm: userInfo?.cd_firm,
          cd_biz: searchInfo?.cd_biz,
          cd_equip: focusedNodeH.data?.cd_equip,
          no_line_param: maxNoLine + 1,
          no_seq: maxSeq + 1,
          fg_range: "1",
          yn_use: "Y",
        };

        return updatedRow;
      } catch (error: any) {
        return false; // 추가 취소
      }
    };
    //#endregion

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onCellValueChanged: onCellValueChangedH,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onBtnAddClick: onGrid2BtnsClick,
            onBtnDeleteClick: onGrid2BtnsClick,
            onBeforeAddRow: onBeforeAddRow2,
          },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="w-full h-full flex flex-col min-h-0">
        <div style={{ display: "none" }}>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div className="w-full flex h-full min-h-0 space-x-1.5">
          <SnResizeBar direction={"width"} id="panel1" orgSize="25%">
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              events={grids.grid1.events}
              etcProps={{
                rowSelection: "single",
                title: "설비그룹",
              }}
              filter={{
                refs: [gridRefs.current["grid2"]],
                columns: ["cd_equip"],
              }}
            />
          </SnResizeBar>
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            events={grids.grid2.events}
            etcProps={{
              rowSelection: "multiple",
              title: "파라미터",
              popupParent: parentref, // 부모 엘리먼트 전달
            }}
            header={
              <div>
                <SnButton
                  text={"저장"}
                  onClick={() => {
                    onGrid2BtnsClick("btnSave");
                  }}
                />
              </div>
            }
          />
        </div>
      </div>
    );
  }
);

export default SN_PRD_BAS_REG_001_CHEM_POP_001;
